import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLoading } from "../../context/LoadingContext";
import SuccessPopup from "../shared/SuccessPopup";
import "./FacilityManagement.css";
import { formatBytes } from "../utils/generalUtilities";

const FacilityManagement = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [systems, setSystems] = useState([]);
  const [systemDetails, setSystemDetails] = useState(null);
  const [activeUsers, setActiveUsers] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [quotaMB, setQuotaMB] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState({
    type: "",
    show: false,
    message: "",
  });

  const [selectedUser, setSelectedUser] = useState(null); // for dialog
  const [showUserSettings, setShowUserSettings] = useState(false);

  const { setLoading } = useLoading();
  const accessToken = sessionStorage.getItem("accessToken");
  const userId = parseInt(localStorage.getItem("id"));
  const headers = { Authorization: `${accessToken}` };
  const selectedSystemId = localStorage.getItem("selectedSystemId");
  const [utilizedStorageSize, setUtilizedStorageSize] = useState(0); // in bytes
  const [itemCount, setItemCount] = useState(0);
  const [showSpaceLimit, setShowSpaceLimit] = useState(false);
  const [spaceLimitOption, setSpaceLimitOption] = useState("");
  const [memberSpaceLimitOption, setMemberSpaceLimitOption] = useState("");

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      const res = await axios.post(
        `${baseUrl}/customer`,
        { userId },
        { headers }
      );
      setCustomers(res.data);
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    const custId = localStorage.getItem("customerId");
    const locId = localStorage.getItem("selectedLocationId");
    const sysId = localStorage.getItem("selectedSystemId");
    const sysName = localStorage.getItem("selectedSystem");

    if (custId) setSelectedCustomer({ id: parseInt(custId) });
    if (locId) setSelectedLocation({ id: parseInt(locId) });
    if (sysId) setSelectedSystem({ id: parseInt(sysId), system_name: sysName });
  }, []);

  const updateUserQuota = async (userId, quotaMB) => {
    try {
      return await axios.put(
        `${baseUrl}/storage/${userId}/quota`,
        { maxBytes: quotaMB * 1024 * 1024 }, // Convert MB to bytes
        { headers }
      );
    } catch (err) {
      console.error(`Failed to update quota for user ${userId}`, err);
      throw err;
    }
  };

  const updateAllUsersQuota = async (quotaMB) => {
    try {
      await axios.put(
        `${baseUrl}/storage/activeUsers/setQuota`,
        {
          systemId: parseInt(selectedSystemId),
          maxBytes: quotaMB * 1024 * 1024,
        },
        { headers }
      );

      setShowSuccessPopup({
        type: "success",
        show: true,
        message: `Quota updated for all ${activeUsers.length} active users.`,
      });

      fetchActiveUsers();
    } catch (err) {
      console.error("Error updating all users quota:", err);
      setShowSuccessPopup({
        type: "warning",
        show: true,
        message: "Failed to update quota for all users.",
      });
    }
  };

  // Fetch locations
  useEffect(() => {
    if (!selectedCustomer) return;
    const fetchLocations = async () => {
      const res = await axios.post(
        `${baseUrl}/customer/locations/${selectedCustomer.id}`,
        { userId },
        { headers }
      );
      setLocations(res.data);
    };
    fetchLocations();
  }, [selectedCustomer]);

  // Fetch systems
  useEffect(() => {
    if (!selectedLocation) return;
    const fetchSystems = async () => {
      const res = await axios.post(
        `${baseUrl}/customer/systems/${selectedLocation.id}`,
        { userId },
        { headers }
      );
      setSystems(res.data);
    };
    fetchSystems();
  }, [selectedLocation]);

  const fetchSystemDetails = async () => {
    try {
      const res = await axios.get(`${baseUrl}/system/${selectedSystemId}`, {
        headers,
      });
      setSystemDetails(res.data);
      if (res.data.maxBytes) {
        setQuotaMB((res.data.maxBytes / (1024 * 1024)).toFixed(2));
      }
    } catch (err) {
      console.error("Failed to fetch system details", err);

      if (err.response && err.response.data?.message === "System not found") {
        try {
          const defaultQuotaMB = 100;
          const defaultQuotaBytes = defaultQuotaMB * 1024 * 1024;

          const createRes = await axios.post(
            `${baseUrl}/system/quota`,
            {
              systemId: parseInt(selectedSystemId),
              maxBytes: defaultQuotaBytes,
              systemName: "Unnamed System",
            },
            { headers }
          );

          // Fetch newly created system details
          const newRes = await axios.get(
            `${baseUrl}/system/${selectedSystemId}`,
            { headers }
          );
          setSystemDetails(newRes.data);
          setQuotaMB(defaultQuotaMB.toString());

          setShowSuccessPopup({
            type: "success",
            show: true,
            message: "Default 100MB storage quota created.",
          });
        } catch (createErr) {
          console.error("Failed to create default quota", createErr);
          setShowSuccessPopup({
            type: "warning",
            show: true,
            message: "Failed to create default quota.",
          });
        }
      }
    }
  };

  useEffect(() => {
    if (!selectedSystemId) return;
    fetchSystemDetails();
  }, []);

  useEffect(() => {
    if (!selectedSystem) return;

    const fetchSystemDetails = async () => {
      try {
        const res = await axios.get(`${baseUrl}/system/${selectedSystem.id}`, {
          headers,
        });
        if (res.data.maxBytes) {
          setCapacity((res.data.maxBytes / (1024 * 1024)).toFixed(2));
        }
      } catch (err) {
        console.error("Failed to fetch system details", err);
      }
    };

    fetchSystemDetails(selectedSystem);
  }, [selectedSystem]);

  const fetchActiveUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${baseUrl}/user/active/${selectedSystemId}`,
        { headers }
      );
      setActiveUsers(res.data.data || []);
    } catch (err) {
      console.error("Error fetching active users:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageInfo = async () => {
    try {
      const { data } = await axios.get(
        `${baseUrl}/storage/storage-size-count/${selectedSystemId}`,
        { headers }
      );
      setUtilizedStorageSize(data.totalSize || 0);
      setItemCount(data.totalFiles || 0);
    } catch (err) {
      console.error("Failed to fetch storage info", err);
    }
  };

  const spaceMBLimitOptions = [
    { value: "Select", label: "Select", disabled: "true" },
    { value: "10", label: "10 MB" },
    { value: "20", label: "20 MB" },
    { value: "50", label: "50 MB" },
    { value: "100", label: "100 MB" },
    { value: "200", label: "200 MB" },
    { value: "500", label: "500 MB" },
  ];

  function SpaceLimitDropdown({
    selectedUser,
    memberSpaceLimitOption,
    setMemberSpaceLimitOption,
  }) {
    const userQuotaMB = selectedUser?.maxQuotaBytes
      ? Math.round(selectedUser.maxQuotaBytes / (1024 * 1024))
      : null;

    let finalOptions = [...spaceMBLimitOptions];

    if (
      userQuotaMB &&
      !finalOptions.some((opt) => opt.value === String(userQuotaMB))
    ) {
      finalOptions.push({
        value: String(userQuotaMB),
        label: `${userQuotaMB} MB`,
      });
    }

    finalOptions = [
      finalOptions.find((o) => o.value === "Select"),
      ...finalOptions
        .filter((o) => o.value !== "Select")
        .sort((a, b) => Number(a.value) - Number(b.value)),
    ];

    return (
      <div className="dropdown mb-3">
        <select
          className="form-select"
          value={
            memberSpaceLimitOption
              ? memberSpaceLimitOption
              : String(userQuotaMB)
          }
          onChange={(e) => setMemberSpaceLimitOption(e.target.value)}
        >
          {finalOptions.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  useEffect(() => {
    if (selectedSystemId) {
      fetchActiveUsers();
      fetchStorageInfo();
    }
  }, [selectedSystemId]);

  const handleSubmit = async () => {
    if (!selectedSystem || !capacity) return;
    const quotaBytes = parseInt(capacity) * 1024 * 1024;

    try {
      await axios.post(
        `${baseUrl}/system/quota`,
        {
          systemId: selectedSystem.id,
          maxBytes: quotaBytes,
          systemName: selectedSystem.system_name,
        },
        { headers }
      );
      setShowSuccessPopup({
        type: "success",
        show: true,
        message: "Storage Quota assigned successfully.",
      });
      fetchSystemDetails();
    } catch (err) {
      setShowSuccessPopup({
        type: "warning",
        show: true,
        message: "Failed to assign quota.",
      });
    }
  };

  return (
    <div className="theme-container container py-2 rounded">
      <div className="d-flex align-items-center">
          <i className="bi bi-people-fill" style={{fontSize: '50px'}}></i>
        <div className="d-flex flex-column ps-1">
          <div className="fnt16 fw-600 py">Enterprise Console</div>
          <div className="fnt14 text-secondary">Enterprise administration and management for your account</div>
        </div>
      </div>

      {/* Activity Dashboard */}
      <div className="card ent-pri-card-bg mb-4 pb-3">
        <div class="card-header">
          <div className="fnt14 fw-500">Activity Dashboard</div>
        </div>
        <div className="card-body">
          <div className="text-center d-flex justify-content-center">
            <div className="border-secondary rounded text-bg-secondary py-1 px-4">
              <div className="fnt13">Data Used</div>
              <div className="d-flex align-items-center">
                <div className="fnt20 fw-500">
                  {" "}
                  {(systemDetails?.usedBytes / (1024 * 1024)).toFixed(2)}
                </div>
                <div className="fnt13 ps-1">MB</div>
              </div>
              <div className="fnt13 text-muted">
                {" "}
                {(systemDetails?.maxBytes / (1024 * 1024)).toFixed(2)} MB
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-around mt-3 small text-muted">
          <div className="border gray-bg rounded py-1 px-5">
            <span className="fw-500">{itemCount}</span> Files
          </div>
          <div className="border gray-bg rounded py-1 px-5">
            <span className="fw-500">{activeUsers.length}</span> Active Users
          </div>
        </div>
      </div>

      {/* Storage Allocation */}
      <div className="card ent-pri-card-bg mb-4 pb-3">
        <div class="card-header">
          <div className="fnt14 fw-500">Storage Allocation</div>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={selectedCustomer?.id || ""}
                onChange={(e) => {
                  const customer = customers.find(
                    (c) => c.id === parseInt(e.target.value)
                  );
                  setSelectedCustomer(customer);
                  setLocations([]);
                  setSelectedLocation(null);
                  setSystems([]);
                  setSelectedSystem(null);
                }}
              >
                <option value="">Select</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Location</label>
              <select
                className="form-select"
                disabled={!locations.length}
                value={selectedLocation?.id || ""}
                onChange={(e) => {
                  const loc = locations.find(
                    (l) => l.id === parseInt(e.target.value)
                  );
                  setSelectedLocation(loc);
                }}
              >
                <option value="">Select</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.plant_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">System</label>
              <select
                className="form-select"
                disabled={!systems.length}
                value={selectedSystem?.id || ""}
                onChange={(e) => {
                  const sys = systems.find(
                    (s) => s.id === parseInt(e.target.value)
                  );
                  setSelectedSystem(sys);
                }}
              >
                <option value="">Select</option>
                {systems.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.system_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Capacity (MB)</label>
              <input
                type="number"
                className="form-control"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                disabled={!selectedSystem}
              />
            </div>

            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-primary w-100"
                onClick={handleSubmit}
                disabled={!capacity || !selectedSystem}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card ent-pri-card-bg mb-4">
        <div class="card-header">
          <div className="fnt14 fw-500">Active Users</div>
        </div>
        <div className="card-body">
          <div className="table-container">
            <table className="active-users-table">
              <thead>
                <tr className="fnt13 decoration-none">
                  <th>
                    <i className="bi bi-person-fill"></i>&nbsp;Name
                  </th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Quota</th>
                  <th>Usage</th>
                  <th>Settings</th>
                </tr>
              </thead>
              <tbody>
                {activeUsers.map((user, idx) => (
                  <tr key={idx} className="fnt13">
                    <td>
                      <i class="bi bi-person-fill-gear"></i>&nbsp;
                      {user.firstname || "N/A"}
                    </td>
                    <td>{user.role_name || "N/A"}</td>
                    <td>{user.email}</td>
                    <td>{formatBytes(user.maxQuotaBytes)}</td>
                    <td>{formatBytes(user.usedQuotaBytes)}</td>
                    <td>
                      <i
                        className="bi bi-gear-fill ps-2"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowUserSettings(true);
                        }}
                        style={{ cursor: "pointer" }}
                      ></i>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showUserSettings && (
        <div
          className="modal fade show show-modal modal--opacity"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content p-3">
              <div className="modal-header">
                <h5 className="modal-title">
                  User Settings – {selectedUser?.firstname}
                </h5>
              </div>

              <ul className="settings-list">
                <li
                  className="settings-item"
                  onClick={() => setShowSpaceLimit(!showSpaceLimit)}
                  style={{ cursor: "pointer" }}
                >
                  <i className="bi bi-pie-chart-fill text-info"></i> Workspace space limit
                </li>
              </ul>

              {showSpaceLimit && (
                <div className="card ent-pri-card-bg mt-3">
                  <div class="card-header">
                    <div className="fnt14 fw-500">Workspace space limit</div>
                  </div>
                  <div className="card-body">
                    <div className="text-secondary mb-1 fnt14">
                      Limit the amount of space this member has in their
                      storage.
                    </div>
                    {SpaceLimitDropdown({
                      selectedUser,
                      memberSpaceLimitOption,
                      setMemberSpaceLimitOption,
                    })}

                    <div className="text-secondary mb-1 fnt14">
                      <p>
                        <strong>Space limit for all members:</strong> Set the
                        amount of space members can use. This doesn’t include
                        team folders.
                      </p>
                    </div>
                    <div className="dropdown mb-3">
                      <select
                        className="form-select"
                        value={spaceLimitOption}
                        onChange={(e) => setSpaceLimitOption(e.target.value)}
                      >
                        {spaceMBLimitOptions.map((opt) => (
                          <option
                            key={opt.value}
                            value={opt.value}
                            disabled={opt.disabled}
                          >
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={() => {
                    setShowSpaceLimit(false);
                    setShowUserSettings(false);
                    setSelectedUser(null);
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    try {
                      if (selectedUser && memberSpaceLimitOption) {
                        const userUpdateResponse = await updateUserQuota(
                          selectedUser.id,
                          parseInt(memberSpaceLimitOption)
                        );

                        if (userUpdateResponse.status === 200) {
                          setShowSuccessPopup({
                            type: "success",
                            show: true,
                            message: `Quota updated for ${selectedUser.firstname}`,
                          });
                          fetchActiveUsers();
                        }
                      }
                      if (
                        spaceLimitOption !== "" &&
                        spaceLimitOption !== "Select"
                      ) {
                        await updateAllUsersQuota(parseInt(spaceLimitOption));
                      }

                      setShowUserSettings(false);
                      setSelectedUser(null);
                      setShowSpaceLimit(false);
                    } catch (err) {
                      setShowSuccessPopup({
                        type: "warning",
                        show: true,
                        message: "Quota update failed.",
                      });
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SuccessPopup
        type={showSuccessPopup?.type}
        show={showSuccessPopup?.show}
        message={showSuccessPopup?.message}
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
  );
};

export default FacilityManagement;
